import * as React from 'react'
import classNames from 'classnames'
import { Combobox, Option } from 'uiComponents/combobox/combobox'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { SegmentedControl, SegmentOption } from 'uiComponents/segmentedControl/segmentedControl'
import { SkeletonBar } from 'uiComponents/skeleton/skeleton'
import { ProductTypeKey, SaleFilter, TypeFilter, typeLabel } from '../model/rowModel'
import { TFunction } from 'i18next'
import { useTranslation } from 'translations'

const getSaleOptions = (t: TFunction): SegmentOption<SaleFilter>[] => [
    { value: 'all', label: t('capacity.products.filterAll') },
    { value: 'yes', label: t('capacity.products.filterYes') },
    { value: 'no', label: t('capacity.products.filterNo') },
]

interface ProductsToolbarProps {
    addOptions: Option[]
    onAdd: (productId: string) => void
    types: ProductTypeKey[]
    typeFilter: TypeFilter
    onTypeFilter: (value: TypeFilter) => void
    saleFilter: SaleFilter
    onSaleFilter: (value: SaleFilter) => void
    shown: number
    total: number
    loading: boolean
}

export function ProductsToolbar({
    addOptions,
    onAdd,
    types,
    typeFilter,
    onTypeFilter,
    saleFilter,
    onSaleFilter,
    shown,
    total,
    loading,
}: ProductsToolbarProps) {
    const { t } = useTranslation()
    const typeOptions = React.useMemo<SegmentOption<TypeFilter>[]>(() => {
        const present = new Set<ProductTypeKey>(types)
        if (typeFilter !== 'all') {
            present.add(typeFilter)
        }
        const sorted = [...present].sort((a, b) => typeLabel(a, true).localeCompare(typeLabel(b, true)))
        return [
            { value: 'all', label: t('capacity.products.filterAll') },
            ...sorted.map((type) => ({ value: type as TypeFilter, label: typeLabel(type, true) })),
        ]
    }, [types, typeFilter])

    const noneLeft = !loading && addOptions.length === 0
    const filtersActive = typeFilter !== 'all' || saleFilter !== 'all'

    return (
        <div className="capacity-toolbar">
            <div className="capacity-toolbar-add">
                <DisabledItemHoverInfotip
                    active={noneLeft}
                    infotipText={t('capacity.products.addNoneLeft')}
                    width="18em"
                    bottom
                >
                    <Combobox
                        options={addOptions}
                        value={null}
                        disabled={noneLeft || undefined}
                        searchPlaceholder={t('capacity.products.addPlaceholder')}
                        onChange={(next) => {
                            if (typeof next === 'string') {
                                onAdd(next)
                            }
                        }}
                    />
                </DisabledItemHoverInfotip>
            </div>
            {loading ? (
                <div className="capacity-filters" aria-hidden="true">
                    <SkeletonBar width="18rem" height="2rem" />
                </div>
            ) : (
                total > 0 && (
                    <div className="capacity-filters">
                        <div className="capacity-filter">
                            <span className="capacity-filter-label">{t('capacity.products.filterType')}</span>
                            <SegmentedControl
                                size="compact"
                                fit
                                ariaLabel={t('capacity.products.filterType')}
                                options={typeOptions}
                                value={typeFilter}
                                onChange={onTypeFilter}
                            />
                        </div>
                        <div className="capacity-filter">
                            <span className="capacity-filter-label">{t('capacity.products.filterOnSale')}</span>
                            <SegmentedControl
                                size="compact"
                                fit
                                ariaLabel={t('capacity.products.filterOnSale')}
                                options={getSaleOptions(t)}
                                value={saleFilter}
                                onChange={onSaleFilter}
                            />
                        </div>
                        <span
                            className={classNames('capacity-filter-count', { hidden: !filtersActive })}
                            aria-hidden={!filtersActive}
                        >
                            {t('capacity.products.count', { shown: shown, count: total })}
                        </span>
                    </div>
                )
            )}
        </div>
    )
}

interface FilteredEmptyProps {
    typeFilter: TypeFilter
    saleFilter: SaleFilter
    onShowAll: () => void
}

export function FilteredEmpty({ typeFilter, saleFilter, onShowAll }: FilteredEmptyProps) {
    const { t } = useTranslation()
    const last = React.useRef<{ type: TypeFilter; sale: SaleFilter }>({ type: 'all', sale: 'all' })
    if (typeFilter !== 'all' || saleFilter !== 'all') {
        last.current = { type: typeFilter, sale: saleFilter }
    }
    const what =
        last.current.type === 'all'
            ? t('capacity.products.filteredEmpty.products')
            : typeLabel(last.current.type, true).toLowerCase()
    return (
        <p className="capacity-table-note">
            {t(
                last.current.sale === 'yes'
                    ? 'capacity.products.filteredEmpty.onSale'
                    : last.current.sale === 'no'
                    ? 'capacity.products.filteredEmpty.notOnSale'
                    : 'capacity.products.filteredEmpty.all',
                { what: what },
            )}{' '}
            <button type="button" className="capacity-text-action" onClick={onShowAll}>
                {t('capacity.products.showAll')}
            </button>
        </p>
    )
}
